// SPDX-License-Identifier: Elastic-2.0
/**
 * NotificationsSection — Settings section for notification preferences
 * (F-320 / T-004).
 *
 * Composes three groups stacked vertically inside a single Card:
 *   1. Channels — push (this browser) · email digest · in-app alerts.
 *   2. Categories — anomalies · suggestions · security.
 *   3. Quiet hours — enable toggle + start/end `<input type="time">`.
 *
 * Persistence: every interaction writes through `useNotificationPrefs`
 * (Zustand + localStorage) immediately — no save button, matching the
 * ThemeSection's immediate-write model.
 *
 * Push toggle is the only control with side-effects beyond local state:
 * it mirrors the live `Notification.permission` value alongside the
 * store and gates the toggle on the browser's permission grant. See
 * the helper `handlePushToggle` + `renderPermissionLine` below.
 *
 * Spec refs:
 *  - Functional: specs/wip/feat-49-notifications-section/functional/notifications-section.md
 *    (§ 2 surface, § 4 push UX, § 5 quiet hours, § 8 accessibility)
 *  - Technical:  specs/wip/feat-49-notifications-section/technical/notifications-section.md
 *    (§ 4 composition + CSS sketch, § 5 SettingsPage wiring)
 */
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { Card } from "@/components/Card/Card";
import { Toggle } from "@/components/Toggle";

import {
  useNotificationPrefs,
  type NotificationChannels,
  type NotificationCategories,
} from "../useNotificationPrefs";
import {
  getPushPermission,
  requestPushPermission,
  type PushPermissionState,
} from "../pushPermission";

import styles from "./NotificationsSection.module.css";

type ChannelKey = keyof NotificationChannels;
type CategoryKey = keyof NotificationCategories;

const NON_PUSH_CHANNELS: readonly { key: Exclude<ChannelKey, "push"> }[] = [
  { key: "email" },
  { key: "in_app" },
];

const CATEGORIES: readonly { key: CategoryKey }[] = [
  { key: "anomalies" },
  { key: "suggestions" },
  { key: "security" },
];

export function NotificationsSection(): JSX.Element {
  const { t } = useTranslation();
  const prefs = useNotificationPrefs((s) => s.prefs);
  const setChannel = useNotificationPrefs((s) => s.setChannel);
  const setCategory = useNotificationPrefs((s) => s.setCategory);
  const setQuietHours = useNotificationPrefs((s) => s.setQuietHours);

  // Mirror the live browser permission alongside the store. We refresh
  // on mount + after a permission request resolves.
  const [permission, setPermission] = useState<PushPermissionState>(() =>
    getPushPermission(),
  );
  useEffect(() => {
    setPermission(getPushPermission());
  }, []);

  const pushUnsupported = permission === "unsupported";
  const titleId = "settings-notifications-title";
  const channelsId = "settings-notifications-channels-eyebrow";
  const categoriesId = "settings-notifications-categories-eyebrow";
  const quietId = "settings-notifications-quiet-eyebrow";

  async function handlePushToggle(next: boolean): Promise<void> {
    if (pushUnsupported) return;
    if (!next) {
      // Turning OFF — just store. We can't programmatically revoke
      // the browser permission.
      setChannel("push", false);
      return;
    }
    // Turning ON — request if needed.
    const current = getPushPermission();
    if (current === "granted") {
      setChannel("push", true);
      return;
    }
    const result = await requestPushPermission();
    setPermission(result);
    setChannel("push", result === "granted");
  }

  const quietHoursWrapsMidnight = useMemo(
    () =>
      prefs.quiet_hours.enabled &&
      prefs.quiet_hours.start > prefs.quiet_hours.end,
    [prefs.quiet_hours],
  );

  return (
    <Card
      as="section"
      variant="elevated"
      padding="md"
      aria-labelledby={titleId}
      data-testid="settings-notifications"
    >
      <h2 id={titleId} className={styles.title}>
        {t("settings.notifications.section_title")}
      </h2>

      {/* Group 1 — channels */}
      <h3 id={channelsId} className={styles.eyebrow}>
        {t("settings.notifications.groups.channels")}
      </h3>
      <div className={styles.group} role="group" aria-labelledby={channelsId}>
        <div
          className={styles.row}
          data-testid="settings-notifications-channel-push"
        >
          <Toggle
            checked={prefs.channels.push}
            onChange={(v) => void handlePushToggle(v)}
            label={t("settings.notifications.channels.push.label")}
            description={t("settings.notifications.channels.push.description")}
            disabled={pushUnsupported}
          />
          {renderPermissionLine(permission, prefs.channels.push, t)}
        </div>
        {NON_PUSH_CHANNELS.map(({ key }) => (
          <div
            key={key}
            className={styles.row}
            data-testid={`settings-notifications-channel-${key}`}
          >
            <Toggle
              checked={prefs.channels[key]}
              onChange={(v) => setChannel(key, v)}
              label={t(`settings.notifications.channels.${key}.label`)}
              description={t(
                `settings.notifications.channels.${key}.description`,
              )}
            />
          </div>
        ))}
      </div>

      {/* Group 2 — categories */}
      <h3 id={categoriesId} className={styles.eyebrow}>
        {t("settings.notifications.groups.categories")}
      </h3>
      <div className={styles.group} role="group" aria-labelledby={categoriesId}>
        {CATEGORIES.map(({ key }) => (
          <div
            key={key}
            className={styles.row}
            data-testid={`settings-notifications-category-${key}`}
          >
            <Toggle
              checked={prefs.categories[key]}
              onChange={(v) => setCategory(key, v)}
              label={t(`settings.notifications.categories.${key}.label`)}
              description={t(
                `settings.notifications.categories.${key}.description`,
              )}
            />
          </div>
        ))}
      </div>

      {/* Group 3 — quiet hours */}
      <h3 id={quietId} className={styles.eyebrow}>
        {t("settings.notifications.groups.quiet_hours")}
      </h3>
      <div className={styles.group} role="group" aria-labelledby={quietId}>
        <div
          className={styles.row}
          data-testid="settings-notifications-quiet-enable"
        >
          <Toggle
            checked={prefs.quiet_hours.enabled}
            onChange={(v) => setQuietHours({ enabled: v })}
            label={t("settings.notifications.quiet_hours.enable_label")}
            description={t(
              "settings.notifications.quiet_hours.enable_description",
            )}
          />
        </div>
        <div className={styles.timeRow}>
          <span className={styles.timeConnective}>
            {t("settings.notifications.quiet_hours.from")}
          </span>
          <input
            type="time"
            value={prefs.quiet_hours.start}
            onChange={(e) => setQuietHours({ start: e.target.value })}
            disabled={!prefs.quiet_hours.enabled}
            aria-label={t("settings.notifications.quiet_hours.start_aria")}
            className={styles.timeInput}
            data-testid="settings-notifications-quiet-start"
          />
          <span className={styles.timeConnective}>
            {t("settings.notifications.quiet_hours.to")}
          </span>
          <input
            type="time"
            value={prefs.quiet_hours.end}
            onChange={(e) => setQuietHours({ end: e.target.value })}
            disabled={!prefs.quiet_hours.enabled}
            aria-label={t("settings.notifications.quiet_hours.end_aria")}
            className={styles.timeInput}
            data-testid="settings-notifications-quiet-end"
          />
        </div>
        {quietHoursWrapsMidnight ? (
          <p
            className={styles.hint}
            data-testid="settings-notifications-quiet-wraps"
          >
            {t("settings.notifications.quiet_hours.wraps_midnight_hint")}
          </p>
        ) : null}
      </div>
    </Card>
  );
}

function renderPermissionLine(
  permission: PushPermissionState,
  pushOn: boolean,
  t: (key: string) => string,
): JSX.Element | null {
  // Only show when relevant. Hidden when permission === "default" AND
  // push is off (means user hasn't tried yet) — avoids visual clutter.
  if (permission === "default" && !pushOn) return null;
  const i18nKey =
    permission === "granted"
      ? "settings.notifications.permission.granted"
      : permission === "denied"
        ? "settings.notifications.permission.denied"
        : permission === "unsupported"
          ? "settings.notifications.permission.unsupported"
          : "settings.notifications.permission.default";
  return (
    <p
      className={styles.permissionLine}
      data-testid="settings-notifications-permission-line"
      data-permission={permission}
    >
      {t(i18nKey)}
    </p>
  );
}

export default NotificationsSection;
